/**
 * Authentication utilities
 * Handles JWT token storage and retrieval
 */

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'user';
const AUTH_KEY = 'isAuthenticated';
const PAGES_PERMISSIONS_KEY = 'pages_permissions';

/**
 * Save authentication token to localStorage
 * @param {string} token - JWT token
 */
export const saveToken = (token) => {
  localStorage.setItem(TOKEN_KEY, token);
};

/**
 * Get authentication token from localStorage
 * @returns {string|null} JWT token or null if not found
 */
export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Remove authentication token from localStorage
 */
export const removeToken = () => {
  localStorage.removeItem(TOKEN_KEY);
};

/**
 * Save user data to localStorage
 * @param {object} user - User object
 */
export const saveUser = (user) => {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  localStorage.setItem(AUTH_KEY, 'true');
};

/**
 * Get user data from localStorage
 * @returns {object|null} User object or null if not found
 */
export const getUser = () => {
  const userStr = localStorage.getItem(USER_KEY);
  return userStr ? JSON.parse(userStr) : null;
};

/**
 * Remove user data from localStorage
 */
export const removeUser = () => {
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(AUTH_KEY);
};

/**
 * Check if user is authenticated
 * @returns {boolean} True if authenticated, false otherwise
 */
export const isAuthenticated = () => {
  const token = getToken();
  const auth = localStorage.getItem(AUTH_KEY);
  return !!token && auth === 'true';
};

/**
 * Save page permissions to localStorage
 * @param {array} pages - Array of pages with permissions
 */
export const savePagesPermissions = (pages) => {
  localStorage.setItem(PAGES_PERMISSIONS_KEY, JSON.stringify(pages));
};

/**
 * Get page permissions from localStorage
 * @returns {array|null} Array of pages with permissions or null if not found
 */
export const getPagesPermissions = () => {
  const pagesStr = localStorage.getItem(PAGES_PERMISSIONS_KEY);
  return pagesStr ? JSON.parse(pagesStr) : null;
};

/**
 * Remove page permissions from localStorage
 */
export const removePagesPermissions = () => {
  localStorage.removeItem(PAGES_PERMISSIONS_KEY);
};

/**
 * Clear all authentication data
 */
export const clearAuth = () => {
  removeToken();
  removeUser();
  removePagesPermissions();
};

/**
 * Get authorization header for API requests
 * @returns {object} Headers object with Authorization header
 */
export const getAuthHeaders = () => {
  const token = getToken();
  return token ? {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  } : {
    'Content-Type': 'application/json'
  };
};

/**
 * Add authorization header to existing headers
 * @param {object} headers - Existing headers object
 * @returns {object} Headers object with Authorization added
 */
export const addAuthHeader = (headers = {}) => {
  const token = getToken();
  return {
    ...headers,
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

